import createInvoice from '~/utils/balance/createInvoice'
import PaymentProcessorsEnum from '~/enums/PaymentProcessorsEnum'
import executeStripePayment from '~/utils/balance/executeStripePayment'
import getSelectedPaymentProcessor from '~/utils/balance/getSelectedPaymentProcessor'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { Routes } from '~/enums/Routes'
import parseRelType from '~/utils/filters/parseRelType'
import type { DestinationInterface } from '~/types/destinationTypes'
import PaymentActionResponseEnum from '~/enums/payments/PaymentActionResponseEnum'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'

interface dataInterface {
  countries: DestinationInterface[]
  depositAmount: number
  paymentProcessorId: number | null
  countryId: number
  sumToPay: number
  stripeToken: string | null
  paymentMethodKey?: string | null
}

export default async (data: dataInterface) => {
  let response: any = {
    action: 'show_error',
    details: 'balance.invalid_payment_processor_selected',
  }

  if (!data.paymentProcessorId) {
    return response
  }

  const selectedPaymentProcessor = await getSelectedPaymentProcessor(
    data.countries,
    data.countryId,
    data.depositAmount,
    data.paymentProcessorId
  )

  if (selectedPaymentProcessor) {
    response.details = 'balance.invalid_request'

    if (parseFloat(selectedPaymentProcessor.amount as string) === data.sumToPay) {
      response.details = 'balance.couldnt_create_invoice'

      const invoice = await createInvoice(data.depositAmount, data.paymentProcessorId, data.countryId)

      if (invoice) {
        response.action = 'redirect'

        if (selectedPaymentProcessor.payment_processor_id === PaymentProcessorsEnum.Stripe) {
          response = await executeStripe(response, invoice, data.stripeToken as string)
        } else {
          const backRoute = getLocalizedRoute(Routes.Account.AccountInformation)
          const router = useRouter()

          response.details = new URL(router.resolve(backRoute).href, window.location.origin).href

          const relType = parseRelType(invoice)

          if (relType !== null) {
            const relId = invoice.substring(2)
            response.details = await redirectToPay(
              relType,
              parseInt(relId),
              data.paymentProcessorId,
              invoice,
              data.paymentMethodKey ?? ''
            )
          }
        }
      }
    }
  }

  return response
}

async function executeStripe(result: any, invoice: string, token: string) {
  const router = useRouter()
  const cancelRoute = getLocalizedRoute(Routes.Payment.Decline)

  result.action = PaymentActionResponseEnum.Redirect
  result.details = new URL(router.resolve(cancelRoute).href, window.location.origin).href

  const response = await executeStripePayment(invoice, token)

  if ('requires_action' in response && response.requires_action) {
    result.action = PaymentActionResponseEnum.StripeRequiresAuthentication
    result.details = {
      payment_intent_client_secret: response.payment_intent_client_secret,
      order_id: response.order_id,
    }
  } else if ('redirect' in response && response.redirect) {
    result.action = PaymentActionResponseEnum.Redirect
    result.details = response.redirect
  } else if ('message' in response && response.message) {
    result.action = PaymentActionResponseEnum.ShowError
    result.details = response.message
  }

  return result
}

async function redirectToPay(relType: number, relId: number, processorId: number, orderId: string, sub: string) {
  const router = useRouter()

  const backRoute = getLocalizedRoute(Routes.Account.AccountInformation)
  let result = new URL(router.resolve(backRoute).href, window.location.origin).href

  const queryParams = { orderId }
  const acceptRoute = getLocalizedRoute(Routes.Payment.Accept, {}, queryParams)
  const declineRoute = getLocalizedRoute(Routes.Payment.Decline, {}, queryParams)

  const locationOrigin = window.location.origin

  const response = (
    await $fetch('/api/payments/generate-payment', {
      method: 'POST',
      body: {
        paymentProcessor: processorId,
        relType,
        relId,
        returnUrl: new URL(router.resolve(acceptRoute).href, locationOrigin).href,
        cancelUrl: new URL(router.resolve(declineRoute).href, locationOrigin).href,
        sub,
      },
      ...fetchRetryParameters,
      headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
    })
  ).data

  if (response?.redirect && response?.redirect_url) {
    result = response.redirect_url
    const paymentProcessorType = response.payment_processor_id ?? null

    if (paymentProcessorType === PaymentProcessorsEnum.ShipzeeBalance && response.paid === true) {
      result = new URL(router.resolve(acceptRoute).href, locationOrigin).href
    }
  }

  return result
}
